import { Page } from 'components/framework/models';
import { RECORD_CLASSES } from 'components/framework/models/mappings';
import { type DocumentTypeEnum_value } from 'components/framework/models/generated/types';
import { type AhaEventMap } from 'global_events';
import AhaDrawer from 'javascripts/drawer';
import terminology from 'javascripts/terminology';
import pluralize from 'javascripts/util/pluralize';
import $ from 'jquery';
import { unmountComponents } from './react_mount';
import {
  createPage,
  findOrCreatePage,
  findPageById,
  findPageByName,
  openPageNew,
} from './util/fetchPages';
import { createRecordLink } from './record_links_creator';

class Research {
  templateModalPath: string;

  constructor(
    private readonly parentId: RecordId,
    private readonly parentType: keyof typeof RECORD_CLASSES,
    private readonly project: RecordId
  ) {}

  async initialize(templateModalPath: string) {
    this.templateModalPath = templateModalPath;
    this.setupInsertDocument();

    /**
     * The add button is a react component and we cannot both reactive preserve
     * it and unmount it with reactive changes, so we need a manual step to
     * unmount and remove it if a reactive change clears the table.
     */
    if ($('.research table').length === 0) {
      unmountComponents($('.research .table-header'));
      $('.research .table-header .create-document-menu').remove();
    }
  }

  setupInsertDocument() {
    const handler = async (event: AhaEventMap['aha.insert-document']) => {
      const { detail } = event;

      if (
        String(this.parentId) === detail.parentId &&
        detail.ctaSource === 'Research'
      ) {
        event.stopPropagation();
        event.stopImmediatePropagation();

        const record = await this.getRecord();

        if (detail.documentType === 'TEMPLATE') {
          await this.createPageInFolderFromTemplate(record);
        } else if (
          detail.documentType === 'INTERNAL_LINK' ||
          detail.documentType === 'EXTERNAL_LINK'
        ) {
          await this.createPageLink(record);
        } else {
          await this.createPageInFolder(record, detail.documentType);
        }
      }
    };

    document.addEventListener('aha.insert-document', handler);

    document.addEventListener(
      'page:load',
      () => document.removeEventListener('aha.insert-document', handler),
      { once: true }
    );
  }

  async getRecord() {
    let recordClass = RECORD_CLASSES[this.parentType];

    return recordClass
      .select('id', 'name', 'referenceNum')
      .merge({ researchFolder: ['id'] })
      .find(String(this.parentId));
  }

  async createPageFromRecord(
    id: string,
    documentType: DocumentTypeEnum_value = Page.DOCUMENT_TYPE_NOTE
  ) {
    const record = await this.getRecord();
    await this.createPageInFolder(record, documentType);
  }

  async createPageInFolderFromTemplate(record) {
    const parentPage = await this.createFolderStructure(record);
    await $.ajax({
      url: this.templateModalPath,
      type: 'GET',
      dataType: 'script',
      data: {
        template_type: 'all',
        parent_id: parentPage.id,
        after_view: 'open_research_drawer',
        record_id: record.id,
        record_name: record.name,
        record_type: this.parentType,
      },
    });
  }

  async createPageInFolder(record, documentType: DocumentTypeEnum_value) {
    const parentPage = await this.createFolderStructure(record);
    const page = await createPage(
      null,
      parentPage.id,
      documentType,
      this.project
    );
    await this.openDrawer(`/pages/${page.id}`);
  }

  async createFolderStructure(record) {
    let featurePage;
    if (record.researchFolder) {
      featurePage = findPageById(
        record.researchFolder.id,
        Page.DOCUMENT_TYPE_NOTE
      );
      if (featurePage) {
        return featurePage;
      }
    }
    const researchPage = await findOrCreatePage(
      'Research',
      null,
      Page.DOCUMENT_TYPE_NOTE,
      this.project
    );

    // We want to use the terminology name for the record type as the folder name
    // but we need to remove the `Ideas::` namespace first.
    //
    // We cannot use `model.terminologyName` because not every model defines it.
    let recordType = this.parentType;
    if (this.parentType === 'Ideas::Idea') {
      recordType = 'Idea';
    }

    const recordPage = await findOrCreatePage(
      pluralize(2, terminology(recordType)),
      researchPage.id,
      Page.DOCUMENT_TYPE_NOTE,
      this.project
    );
    // The research folder used to just be the record name.
    // This was updated to be a reference num + name. However,
    // because there wasn't a hard link between record & folder
    // we need to check both.
    featurePage = await findPageByName(
      record.name,
      recordPage.id,
      Page.DOCUMENT_TYPE_NOTE,
      this.project
    );
    if (!featurePage) {
      featurePage = await findOrCreatePage(
        `${record.referenceNum} - ${record.name}`,
        recordPage.id,
        Page.DOCUMENT_TYPE_NOTE,
        this.project
      );
    }
    record.researchFolder = { id: featurePage.id };
    await record.save();

    return featurePage;
  }

  async createPageLink(record) {
    const parent = await this.createFolderStructure(record);

    openPageNew(this.project, parent);
  }

  async openDrawer(recordPath: string) {
    // @ts-ignore
    $('#modal-window').modal('hide');
    AhaDrawer.showUrl(recordPath);
  }

  async createLinkAndOpenDrawer(
    recordId: string,
    recordType: string,
    recordPath: string
  ) {
    await createRecordLink(
      { id: this.parentId, type: this.parentType },
      { id: recordId, type: recordType }
    );
    await this.openDrawer(recordPath);
  }
}

export default Research;
